import styled, { css, keyframes } from 'styled-components'
import { Colors, Spacing } from 'cio-design-library/design-tokens'
import Typography, { FontWeights } from 'builder/styles/typography'
import Media from 'builder/styles/media'
import Icon from 'builder/components/Icon'
import { gradient } from 'builder/styles/keyframes'
import Button from 'builder/components/Button'
import { AvatarNavigation } from 'builder/components/AvatarNavigation'
import { JobSearchSolution } from 'builder/views/AutoApply/components/JobSearchSolution/JobSearchSolution'
import { foldableSectionMixin } from 'builder/components/Section'

const animatedBorder = keyframes`
  0% {
    background: linear-gradient(white, white) padding-box,
      linear-gradient(0deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  25% {
    background: linear-gradient(white, white) padding-box,
      linear-gradient(90deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  50% {
    background: linear-gradient(white, white) padding-box,
      linear-gradient(180deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  75% {
    background: linear-gradient(white, white) padding-box,
      linear-gradient(270deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  100% {
    background: linear-gradient(white, white) padding-box,
      linear-gradient(360deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
`

const animatedBorderHover = keyframes`
  0% {
    background: linear-gradient(${Colors.Indigo10}, ${Colors.Indigo10}) padding-box,
      linear-gradient(0deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  25% {
    background: linear-gradient(${Colors.Indigo10}, ${Colors.Indigo10}) padding-box,
      linear-gradient(90deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  50% {
    background: linear-gradient(${Colors.Indigo10}, ${Colors.Indigo10}) padding-box,
      linear-gradient(180deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  75% {
    background: linear-gradient(${Colors.Indigo10}, ${Colors.Indigo10}) padding-box,
      linear-gradient(270deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
  100% {
    background: linear-gradient(${Colors.Indigo10}, ${Colors.Indigo10}) padding-box,
      linear-gradient(360deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  }
`

type OptimizerWrapperProps = {
  $isPageScrolled?: boolean
  $stickyTop: number
}

const mobileSwitcherHeight = 58

export const EditorPanel = styled.div`
  flex: 1;
  max-width: 100%;
  height: 100%;
  background-color: ${Colors.Neutral10};
  margin: auto;

  ${Media.Phone`
    height: unset;
  `};
`

export const EditorPanelWithTabs = styled.div`
  flex: 1;
  max-width: 100%;
  height: 100vh;
  background-color: ${Colors.Neutral10};
  display: flex;
  flex-direction: column;

  ${Media.Tablet`
    height: unset;
    min-height: 100vh;
  `};
`

export const EditorPanelCoverLetter = styled.div`
  flex: 1;
  max-width: 100%;
  height: 100vh;
  background-color: ${Colors.Neutral10};
`

export const EditorScroll = styled.div``

export const EditorScrollCoverLetter = styled.div`
  height: 100%;
  background: ${Colors.White};
`

export const EditorContent = styled.div`
  position: relative;
`

export const EditorContentCoverLetter = styled.div`
  position: relative;
  background-color: ${Colors.Neutral10};

  padding-bottom: 88px;

  ${Media.Phone`
    padding-bottom: 70px;
  `};
`

export const EditorTitleWrapper = styled.div`
  background-color: ${Colors.White};
  padding: 48px 36px 0;

  ${Media.Tablet`
    border-top-left-radius: ${Spacing.XS};
    border-top-right-radius: ${Spacing.XS};
    padding: 24px 24px 0;
  `};

  ${Media.Phone`
    padding: 16px 20px 0;
  `};
`

export const FloatingButton = styled(Button)`
  position: sticky;
  bottom: 24px;
  margin: 0 auto;
  z-index: 1000;
  display: flex;
  border-radius: 32px;
  background: linear-gradient(white, white) padding-box,
    linear-gradient(135deg, #ee5e1b 0%, #87b6f7 50%, #7971b0 100%) border-box;
  border: 2.5px solid transparent;
  box-shadow: 0 2px 4px -1px rgba(15, 56, 113, 0.08), 0 8px 20px -4px rgba(15, 56, 113, 0.12);
  gap: 4px;
  align-items: center;
  ${Typography.Caption};
  ${FontWeights.Medium};
  color: ${Colors.Neutral90};
  animation: ${animatedBorder} 1.6s ease-in-out infinite;

  &:hover {
    animation: ${animatedBorderHover} 1.6s ease-in-out infinite;
  }

  ${Media.Tablet`
    bottom: 76px;
  `};

  ${Media.Phone`
    bottom: 72px;
  `};
`

export const ResumeScoreWrapper = styled.div<OptimizerWrapperProps>(props => {
  const { $isPageScrolled, $stickyTop } = props
  return css`
    ${foldableSectionMixin};
    border-radius: 0;
    margin-bottom: 0;
    position: sticky;
    top: ${$stickyTop}px;
    // same as in ResumeScore.tsx
    z-index: ${$isPageScrolled ? 4 : 2};
    padding: 0 36px;

    ${Media.Tablet`
      top: ${$stickyTop + mobileSwitcherHeight}px;
    `};

    ${Media.Phone`
      padding: 0 20px;
    `};
  `
})

export const OptimizerBannerWrapper = styled.div`
  ${foldableSectionMixin};
  margin-bottom: 0;
  border-radius: 0;
  padding: 16px 36px 0;

  ${Media.Phone`
    padding: 16px 20px 0;
  `};
`

export const ResumeHeaderBottomSpace = styled.div`
  ${foldableSectionMixin};
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  height: ${Spacing.M};
`

export const EditorSubtitle = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 12px;
`

export const EditorTitleSeparator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${Colors.Neutral20};
  margin-bottom: 32px;
`

export const LoaderContainer = styled.div`
  animation: ${gradient} 1s linear infinite;
  background: ${Colors.Neutral10};
  background: linear-gradient(
    to right,
    ${Colors.Neutral10} 5%,
    #eaeef6 15%,
    ${Colors.Neutral10} 25%
  );
  background-size: 100vw 100vh;
  height: 100vh;
  position: relative;
  overflow: hidden;
`

export const Loader = styled.img`
  height: 1044px;
  width: 100%;
`

export const Note = styled.a`
  display: flex;
  align-items: center;
  ${Typography.Caption};
  color: ${Colors.Neutral50};
  transition: color 0.2s;

  &:hover {
    color: ${Colors.Blue70};
  }
`

export const NoteIcon = styled(Icon.HintOutlined)`
  width: 20px;
  height: 20px;
  color: ${Colors.Blue50};
  margin-right: 8px;
`

export const NoteText = styled.span``

export const UserNavigation = styled(AvatarNavigation)`
  position: fixed;
  top: 16px;
  right: 16px;
  margin-left: 0;
  z-index: 50;

  ${Media.Laptop`
    position: absolute;
  `};
`

export const JobSearchContainer = styled.div`
  z-index: 3;
  top: 6px;
  position: absolute;
  width: 270px;
  left: calc(50% - 270px);

  @media (min-width: 1900px) {
    left: calc(960px - 270px);
  }

  ${Media.Laptop`
    top: 6px;
    position: absolute;
    left: unset;
    right: 40px;
    width: 300px;
    margin-top: 0;
  `};

  ${Media.Phone`
    position: unset;
    width: unset;
    float: unset;
    padding: 24px 20px 5px;
    background-color: ${Colors.White};

    &:empty {
      display: none;
    }
  `}
`

export const ResumeDistributionActionContainer = styled.div`
  top: 6px;
  position: absolute;
  right: 16px;
  z-index: 2;

  ${Media.Laptop`
    right: 60px;
  `};

  ${Media.Phone`
    position: unset;
    width: unset;
    float: unset;
    margin-bottom: 12px;
  `}
`

export const VerticalDivider = styled.div`
  display: none;
  width: 1px;
  border-right: 1px solid ${Colors.Neutral15};
  height: 24px;
  margin: 0 24px;

  ${Media.Laptop`
    display: block;
    position: absolute;
    right: 0;
    top: 15px;
  `};

  ${Media.Phone`
    display: none;
  `}
`

export const JobSearch = styled(JobSearchSolution)`
  width: fit-content;

  ${Media.Laptop`
    position: relative;
  `}

  ${Media.Phone`
    position: relative;
    width: 100%;
  `}
`

export const Separator = styled.div`
  margin-bottom: 24px;
`

export const RegenerateSection = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 40px;

  ${Media.Phone`
    flex-direction: column;
  `};
`

export const RemainingAttemptsText = styled.p`
  ${Typography.Body};
  color: ${Colors.Indigo60};
  margin-left: 16px;
  ${Media.Phone`
    margin-top: 16px;
  `};
`

export const PreviewButton = styled(Button)`
  display: none;
  position: fixed;
  right: 24px;
  bottom: 24px;
  border-radius: 36px;
  gap: 12px;
  align-items: center;

  ${Media.Tablet`
    display: flex;
  `};

  ${Media.Phone`
    display: none;
  `};
`

export const MobileTopSpacer = styled.div`
  background-color: ${Colors.White};
  height: 64px;

  ${Media.Phone`
    height: 20px;
  `};
`

export const EditorPanelSwitcherWrapper = styled.div<{ $stickyTop?: number }>`
  ${foldableSectionMixin};
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  padding: ${Spacing['2XS']} ${Spacing.XS};
  background-color: ${Colors.White};
  position: sticky;
  top: ${props => props.$stickyTop || 0}px;
  z-index: 5;
`

export const TabletTabsRow = styled.div`
  display: none;

  ${Media.Tablet`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: ${Colors.White};
    border-bottom: 1px solid ${Colors.Neutral15};
  `};
`

export const MenuContainer = styled.div`
  background-color: ${Colors.White};
  position: fixed;
  top: 64px;
  left: 0;
  z-index: 600;
  height: calc(100vh - 64px);
  border-right: 1px solid ${Colors.Neutral15};

  ${Media.Phone`
    display: none;
  `};
`

export const MobileTabsWrapper = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`
